import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Clock,
  FileText,
  IndianRupee,
  Info,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { ToolLayout } from '@layouts/ToolLayout';

import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import { ResultSummaryCard } from '@ui/ResultSummaryCard';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import {
  formatCurrency,
  formatIndianNumberShort,
} from '@utils/numberFormatter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const SIPDelayCalculator = () => {
  // SIP Delay Calculator State
  const [monthlyInvestment, setMonthlyInvestment] = useState<number | null>(
    5000
  );
  const [expectedReturn, setExpectedReturn] = useState<number | null>(12);
  const [timePeriod, setTimePeriod] = useState<number | null>(10);
  const [delayInMonths, setDelayInMonths] = useState<number | null>(5);
  const [results, setResults] = useState({
    maturityWithoutDelay: 0,
    maturityWithDelay: 0,
    totalInvestmentWithoutDelay: 0,
    totalInvestmentWithDelay: 0,
    potentialLoss: 0,
    extraMonthlyRequired: 0,
    returnsWithoutDelay: 0,
    returnsWithDelay: 0,
  });

  // Calculate SIP delay cost
  const calculateDelayImpact = useCallback(() => {
    // Handle null values by using defaults
    const monthlyInv = monthlyInvestment || 0;
    const returnRate = expectedReturn || 0;
    const period = timePeriod || 0;
    const delay = delayInMonths || 0;

    const monthlyRate = returnRate / 100 / 12;
    const totalMonths = period * 12;
    const delayedMonths = totalMonths - delay;

    // Calculate without delay (full period)
    let maturityWithoutDelay = 0;
    let totalInvestmentWithoutDelay = monthlyInv * totalMonths;

    if (monthlyRate === 0) {
      maturityWithoutDelay = totalInvestmentWithoutDelay;
    } else {
      maturityWithoutDelay =
        monthlyInv *
        (((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
          (1 + monthlyRate));
    }

    // Calculate with delay (reduced period)
    let maturityWithDelay = 0;
    let totalInvestmentWithDelay = monthlyInv * delayedMonths;

    if (delayedMonths <= 0) {
      maturityWithDelay = 0;
      totalInvestmentWithDelay = 0;
    } else if (monthlyRate === 0) {
      maturityWithDelay = totalInvestmentWithDelay;
    } else {
      maturityWithDelay =
        monthlyInv *
        (((Math.pow(1 + monthlyRate, delayedMonths) - 1) / monthlyRate) *
          (1 + monthlyRate));
    }

    const potentialLoss = maturityWithoutDelay - maturityWithDelay;
    const returnsWithoutDelay =
      maturityWithoutDelay - totalInvestmentWithoutDelay;
    const returnsWithDelay = maturityWithDelay - totalInvestmentWithDelay;

    // Calculate extra monthly investment needed to achieve the same goal
    let extraMonthlyRequired = 0;
    if (delayedMonths > 0 && monthlyRate > 0) {
      // Target maturity value = maturityWithoutDelay
      // Required monthly investment = Target / SIP formula
      const requiredMonthly =
        maturityWithoutDelay /
        (((Math.pow(1 + monthlyRate, delayedMonths) - 1) / monthlyRate) *
          (1 + monthlyRate));
      extraMonthlyRequired = requiredMonthly - monthlyInv;
    }

    setResults({
      maturityWithoutDelay,
      maturityWithDelay,
      totalInvestmentWithoutDelay,
      totalInvestmentWithDelay,
      potentialLoss,
      extraMonthlyRequired: Math.max(0, extraMonthlyRequired),
      returnsWithoutDelay,
      returnsWithDelay,
    });
  }, [monthlyInvestment, expectedReturn, timePeriod, delayInMonths]);

  // Generate chart data for comparison
  const generateChartData = () => {
    // Handle null values by using defaults
    const monthlyInv = monthlyInvestment || 0;
    const returnRate = expectedReturn || 0;
    const period = timePeriod || 0;
    const delay = delayInMonths || 0;

    const years = [];
    const withoutDelayData = [];
    const withDelayData = [];

    const monthlyRate = returnRate / 100 / 12;

    for (let year = 0; year <= period; year++) {
      years.push(year === 0 ? 'Start' : `Year ${year}`);

      // Without delay calculation
      const monthsWithoutDelay = year * 12;
      let valueWithoutDelay = 0;

      if (monthsWithoutDelay > 0) {
        if (monthlyRate === 0) {
          valueWithoutDelay = monthlyInv * monthsWithoutDelay;
        } else {
          valueWithoutDelay =
            monthlyInv *
            (((Math.pow(1 + monthlyRate, monthsWithoutDelay) - 1) /
              monthlyRate) *
              (1 + monthlyRate));
        }
      }

      // With delay calculation
      const monthsWithDelay = Math.max(0, year * 12 - delay);
      let valueWithDelay = 0;

      if (monthsWithDelay > 0) {
        if (monthlyRate === 0) {
          valueWithDelay = monthlyInv * monthsWithDelay;
        } else {
          valueWithDelay =
            monthlyInv *
            (((Math.pow(1 + monthlyRate, monthsWithDelay) - 1) / monthlyRate) *
              (1 + monthlyRate));
        }
      }

      withoutDelayData.push(valueWithoutDelay);
      withDelayData.push(valueWithDelay);
    }

    return {
      labels: years,
      datasets: [
        {
          label: 'Without delay',
          data: withoutDelayData,
          borderColor: COLORS.sage,
          backgroundColor: `${COLORS.sage}20`,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: COLORS.sage,
          pointBorderColor: COLORS.sage,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: `With ${delay} month delay`,
          data: withDelayData,
          borderColor: '#ef4444',
          backgroundColor: '#ef444420',
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#ef4444',
          pointBorderColor: '#ef4444',
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  // Generate table data for year-wise comparison
  const generateTableData = () => {
    // Handle null values by using defaults
    const monthlyInv = monthlyInvestment || 0;
    const returnRate = expectedReturn || 0;
    const period = timePeriod || 0;
    const delay = delayInMonths || 0;

    const tableData = [];
    const monthlyRate = returnRate / 100 / 12;

    for (let year = 1; year <= period; year++) {
      // Without delay calculation
      const monthsWithoutDelay = year * 12;
      const investmentWithoutDelay = monthlyInv * monthsWithoutDelay;
      let valueWithoutDelay = 0;

      if (monthlyRate === 0) {
        valueWithoutDelay = investmentWithoutDelay;
      } else {
        valueWithoutDelay =
          monthlyInv *
          (((Math.pow(1 + monthlyRate, monthsWithoutDelay) - 1) / monthlyRate) *
            (1 + monthlyRate));
      }

      // With delay calculation
      const monthsWithDelay = Math.max(0, year * 12 - delay);
      const investmentWithDelay = monthlyInv * monthsWithDelay;
      let valueWithDelay = 0;

      if (monthsWithDelay > 0) {
        if (monthlyRate === 0) {
          valueWithDelay = investmentWithDelay;
        } else {
          valueWithDelay =
            monthlyInv *
            (((Math.pow(1 + monthlyRate, monthsWithDelay) - 1) / monthlyRate) *
              (1 + monthlyRate));
        }
      }

      const lossAtYear = valueWithoutDelay - valueWithDelay;

      tableData.push({
        year,
        investmentWithoutDelay,
        valueWithoutDelay,
        investmentWithDelay,
        valueWithDelay,
        lossAtYear,
        returnsWithoutDelay: valueWithoutDelay - investmentWithoutDelay,
        returnsWithDelay: valueWithDelay - investmentWithDelay,
      });
    }

    return tableData;
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: COLORS.accent,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            const value = `₹${context.parsed.y.toLocaleString('en-IN')}`;
            return `${context.dataset.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.smoke,
        },
      },
      y: {
        grid: {
          color: `${COLORS.smoke}20`,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.charcoal,
          callback: function (value: any) {
            return `₹${value.toLocaleString('en-IN')}`;
          },
        },
        title: {
          display: true,
          text: 'Amount (₹)',
          color: COLORS.charcoal,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  // Recalculate when inputs change
  useEffect(() => {
    calculateDelayImpact();
  }, [calculateDelayImpact]);

  return (
    <ToolLayout
      title={`SIP delay cost calculator | ${CONTACT_INFO.company.name}`}
      heroTitle="SIP delay cost calculator"
      description="Calculate the cost of delaying your SIP investments and see how procrastination affects your wealth creation"
      canonicalUrl="https://niveshright.com/tools/sip-delay-calculator"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.gold}20` }}
        >
          <Clock size={32} style={{ color: COLORS.gold }} />
        </div>
      }
      showBreadcrumb={true}
      keywords="SIP delay calculator, cost of delay, SIP procrastination, investment delay impact, SIP timing, systematic investment plan delay"
      cta={
        <WhatsAppCTA
          title="Don't let delay cost you your financial goals!"
          description="Start your SIP journey today with expert guidance and avoid the cost of procrastination."
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={`Hi ${CONTACT_INFO.contact_person.short_name}! I used your SIP delay calculator with these details:

Monthly SIP: ${formatCurrency(monthlyInvestment || 0)}
Investment period: ${timePeriod || 0} years
Expected return: ${expectedReturn || 0}% p.a.
Delay: ${delayInMonths || 0} months

DELAY IMPACT RESULTS:
• Without delay: ₹${results.maturityWithoutDelay.toLocaleString('en-IN')}
• With delay: ₹${results.maturityWithDelay.toLocaleString('en-IN')}
• Potential loss: ₹${results.potentialLoss.toLocaleString('en-IN')}
• Extra monthly needed: ₹${results.extraMonthlyRequired.toLocaleString('en-IN')}

I understand the importance of starting early and want to begin my SIP investment immediately. Can you help me get started?`}
          buttonText="Start SIP now"
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.accent}
        />
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <h2
            className="text-2xl font-bold mb-8"
            style={{ color: COLORS.primary }}
          >
            Investment details
          </h2>

          {/* Monthly Investment */}
          <NumberInputWithSlider
            label="Monthly SIP amount"
            value={monthlyInvestment}
            onChange={setMonthlyInvestment}
            min={500}
            max={1000000000}
            step={500}
            placeholder="5000"
            prefix="₹"
            showTooltip={true}
            minLabel="₹500"
            maxLabel={`₹${formatIndianNumberShort(1000000000, false)}`}
            className="mb-8"
          />

          {/* Expected Return */}
          <NumberInputWithSlider
            label="Expected annual return (%)"
            value={expectedReturn}
            onChange={setExpectedReturn}
            min={1}
            max={100}
            step={0.5}
            placeholder="12"
            minLabel="1%"
            maxLabel="100%"
            className="mb-8"
          />

          {/* Time Period */}
          <NumberInputWithSlider
            label="Investment period (years)"
            value={timePeriod}
            onChange={setTimePeriod}
            min={1}
            max={85}
            step={1}
            placeholder="10"
            minLabel="1 year"
            maxLabel="85 years"
            className="mb-8"
          />

          {/* Delay in Months */}
          <NumberInputWithSlider
            label="Delay in starting SIP (months)"
            value={delayInMonths}
            onChange={setDelayInMonths}
            min={0}
            max={120}
            step={1}
            placeholder="5"
            minLabel="0 months"
            maxLabel="120 months"
            className="mb-8"
          />
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-6">
            <ResultSummaryCard
              title="Without delay"
              value={<LocalizedCurrency value={results.maturityWithoutDelay} />}
              subtitle={`Maturity value after ${timePeriod} years`}
              icon={Target}
              iconColor={COLORS.sage}
              valueColor={COLORS.sage}
              delay={0.6}
            />

            <ResultSummaryCard
              title="With delay"
              value={<LocalizedCurrency value={results.maturityWithDelay} />}
              subtitle={`Maturity value with ${delayInMonths} month delay`}
              icon={Clock}
              iconColor={COLORS.accent}
              valueColor={COLORS.accent}
              delay={0.7}
            />

            <ResultSummaryCard
              title="Potential loss"
              value={<LocalizedCurrency value={results.potentialLoss} />}
              subtitle="Wealth lost due to delay"
              icon={TrendingUp}
              iconColor="#ef4444"
              valueColor="#ef4444"
              delay={0.8}
            />

            {results.extraMonthlyRequired > 0 && (
              <ResultSummaryCard
                title="Extra monthly needed"
                value={
                  <LocalizedCurrency value={results.extraMonthlyRequired} />
                }
                subtitle="Additional SIP to achieve same goal"
                icon={IndianRupee}
                iconColor={COLORS.gold}
                valueColor={COLORS.gold}
                delay={0.9}
              />
            )}
          </div>

          {/* Impact Visualization */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <div className="flex items-center mb-6">
              <BarChart3
                size={24}
                style={{ color: COLORS.gold }}
                className="mr-3"
              />
              <h3
                className="text-lg font-semibold"
                style={{ color: COLORS.primary }}
              >
                Delay impact visualization
              </h3>
            </div>

            <div className="h-80">
              <Line data={generateChartData()} options={chartOptions} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 text-center">
              <div className="p-3 bg-green-50 rounded-xl">
                <p className="text-sm font-medium text-gray-600">
                  Without delay
                </p>
                <p className="text-lg font-bold" style={{ color: COLORS.sage }}>
                  <LocalizedCurrency value={results.maturityWithoutDelay} />
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-xl">
                <p className="text-sm font-medium text-gray-600">With delay</p>
                <p className="text-lg font-bold text-red-600">
                  <LocalizedCurrency value={results.maturityWithDelay} />
                </p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl text-center">
              <p className="text-sm font-medium text-gray-600 mb-1">
                Cost of delay
              </p>
              <p className="text-2xl font-bold text-red-600">
                <LocalizedCurrency value={results.potentialLoss} />
              </p>
              <p className="text-sm text-gray-500">
                {results.maturityWithoutDelay > 0
                  ? (
                      (results.potentialLoss / results.maturityWithoutDelay) *
                      100
                    ).toFixed(1)
                  : 0}
                % of potential wealth lost
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Detailed Comparison Table */}
      <motion.div
        className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mt-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0 }}
      >
        <div className="flex items-center mb-6">
          <FileText size={24} style={{ color: COLORS.gold }} className="mr-3" />
          <h3
            className="text-lg font-semibold"
            style={{ color: COLORS.primary }}
          >
            Year-wise delay impact breakdown
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th
                  className="text-left py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Year
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Without delay
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  With delay
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Loss at year
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Returns lost
                </th>
              </tr>
            </thead>
            <tbody>
              {generateTableData().map((row, index) => (
                <tr
                  key={row.year}
                  className={`border-b border-gray-100 ${
                    index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                  } hover:bg-blue-50 transition-colors duration-200`}
                >
                  <td
                    className="py-3 px-2 font-medium"
                    style={{ color: COLORS.primary }}
                  >
                    Year {row.year}
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{ color: COLORS.sage }}
                  >
                    <LocalizedCurrency value={row.valueWithoutDelay} />
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{ color: COLORS.accent }}
                  >
                    <LocalizedCurrency value={row.valueWithDelay} />
                  </td>
                  <td className="py-3 px-2 text-right font-medium text-red-600">
                    <LocalizedCurrency value={row.lossAtYear} />
                  </td>
                  <td className="py-3 px-2 text-right font-medium text-red-600">
                    <LocalizedCurrency
                      value={row.returnsWithoutDelay - row.returnsWithDelay}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 p-4 bg-red-50 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Final loss in corpus
              </p>
              <p className="text-lg font-bold text-red-600">
                <LocalizedCurrency value={results.potentialLoss} />
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Returns lost</p>
              <p className="text-lg font-bold text-red-600">
                <LocalizedCurrency
                  value={results.returnsWithoutDelay - results.returnsWithDelay}
                />
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Delay cost per month
              </p>
              <p className="text-lg font-bold text-red-600">
                <LocalizedCurrency
                  value={
                    (delayInMonths || 0) > 0
                      ? results.potentialLoss / (delayInMonths || 1)
                      : 0
                  }
                />
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200 text-xs">
          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            <div>
              <ul className="space-y-3">
                <li>
                  <strong>Without delay:</strong> Portfolio value if you start
                  SIP immediately
                </li>
                <li>
                  <strong>With delay:</strong> Portfolio value if you delay
                  starting by specified months
                </li>
                <li>
                  <strong>Loss at year:</strong> Difference in portfolio value
                  at each year due to delay
                </li>
              </ul>
            </div>
            <div>
              <ul className="space-y-3">
                <li>
                  <strong>Returns lost:</strong> Additional returns you could
                  have earned without delay
                </li>
                <li>
                  <strong>Delay cost per month:</strong> Average monthly cost of
                  procrastination
                </li>
                <li>
                  <strong>Extra monthly needed:</strong> Additional SIP amount
                  required to achieve the same goal after delay
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Disclaimer */}
      <motion.div
        className="mt-12 max-w-5xl mx-auto px-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0 }}
      >
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 shadow-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Info size={24} className="text-amber-600" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-bold text-amber-800 mb-3">
                Disclaimer
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold text-amber-800 mb-2 text-sm">
                    Important considerations:
                  </p>
                  <ul className="space-y-1 text-sm text-amber-700">
                    <li>
                      • <strong>Returns are not guaranteed</strong>
                    </li>
                    <li>• Actual returns may vary significantly</li>
                    <li>• Market volatility affects outcomes</li>
                    <li>• Past performance ≠ future results</li>
                    <li>• Calculations are estimates only</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-amber-800 mb-2 text-sm">
                    Professional advice:
                  </p>
                  <ul className="space-y-1 text-sm text-amber-700">
                    <li>
                      • <strong>Consult a certified financial advisor</strong>
                    </li>
                    <li>• Review your risk tolerance</li>
                    <li>• Consider your financial goals</li>
                    <li>• Diversify your investments</li>
                    <li>• Start investing as early as possible</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 p-3 bg-amber-100 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> This calculator demonstrates the impact
                  of delaying investments based on assumed returns. Actual
                  investment performance may differ. Always seek professional
                  financial advice before making investment decisions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Educational Content */}
      <motion.div
        className="mt-16 max-w-5xl mx-auto px-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.1 }}
      >
        <article className="prose prose-lg md:prose-xl max-w-none">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">
            Why delaying SIP is costly?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            <strong>"Time is money".</strong> This saying is never more true
            than in investing. When you delay starting your SIP, you're not just
            losing the money you could have invested, but also the{' '}
            <em>compound returns</em> that money could have generated over time.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            The power of compounding
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            Compounding is like a snowball rolling down a hill, it starts small
            but grows exponentially. The earlier you start, the bigger your
            snowball becomes. Every month you delay reduces the time your money
            has to compound, resulting in significantly lower wealth creation.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Real cost of delay
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            The cost of delay isn't just the monthly SIP amount you missed. It's
            the entire future value that amount could have grown to. For
            example, if you delay a ₹5,000 monthly SIP by just 6 months, you're
            not losing ₹30,000, you're losing the future value of that ₹30,000
            over your entire investment horizon.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Common reasons for delay and solutions
          </h3>

          <div className="space-y-6 mb-8 md:mb-10">
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                "I'll start next month"
              </h4>
              <p className="text-gray-700">
                <strong>Solution:</strong> Start with whatever amount you can
                afford today, even if it's just ₹500. You can always increase it
                later using step-up SIP.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                "I'm waiting for the right time"
              </h4>
              <p className="text-gray-700">
                <strong>Solution:</strong> There's no perfect time to start
                investing. SIP's rupee cost averaging takes care of market
                timing for you.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                "I need to learn more first"
              </h4>
              <p className="text-gray-700">
                <strong>Solution:</strong> Start with simple, diversified equity
                mutual funds while you learn. The cost of delay is higher than
                the risk of starting with basic knowledge.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                "I don't have enough money"
              </h4>
              <p className="text-gray-700">
                <strong>Solution:</strong> Most SIPs can be started with as
                little as ₹500 per month. Cut one unnecessary expense and start
                your wealth journey.
              </p>
            </div>
          </div>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            The 8th wonder of the world
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            Albert Einstein reportedly called compound interest "the eighth
            wonder of the world." Those who understand it, earn it. Those who
            don't, pay it. By delaying your SIP, you're missing out on this
            wonder working in your favor.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Action steps to avoid delay
          </h3>
          <div className="space-y-4 md:space-y-5 mb-8 md:mb-10">
            <p className="text-lg text-gray-700">
              <strong>1. Start today:</strong> Even if it's a small amount,
              start your first SIP today
            </p>
            <p className="text-lg text-gray-700">
              <strong>2. Automate it:</strong> Set up automatic deduction so you
              don't have to think about it
            </p>
            <p className="text-lg text-gray-700">
              <strong>3. Increase gradually:</strong> Use step-up SIP to
              increase your investment as your income grows
            </p>
            <p className="text-lg text-gray-700">
              <strong>4. Stay consistent:</strong> Don't stop and start, let
              compounding work its magic
            </p>
            <p className="text-lg text-gray-700">
              <strong>5. Seek guidance:</strong> If unsure, consult a financial
              advisor but don't let analysis paralysis delay your start
            </p>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-orange-50 p-8 rounded-2xl border border-red-200 text-center">
            <h4 className="text-2xl font-bold text-red-700 mb-4">
              Remember: Every day you delay is money lost forever
            </h4>
            <p className="text-lg text-red-600">
              The best time to start investing was yesterday. The second best
              time is today. Don't let procrastination cost you your financial
              future.
            </p>
          </div>

          <p className="mt-8">
            For more insights on SIP investing, check out our guides on{' '}
            <a
              href="/guides/sip-investment-strategy"
              className="text-blue-600 hover:underline"
            >
              SIP investment strategy
            </a>{' '}
            and{' '}
            <a
              href="/guides/investing-in-mutual-funds"
              className="text-blue-600 hover:underline"
            >
              investing in mutual funds
            </a>
            .
          </p>
        </article>
      </motion.div>
    </ToolLayout>
  );
};
